/**
 * Variant path prefix for the router — LOCAL DEVELOPMENT ONLY.
 *
 * Served locally, the product shows in the URL — `/cca/manage-portfolio`,
 * `/eia/manage-portfolio` — because on localhost a port is the only other thing
 * telling the two apart, and a URL that names the product is easier to read,
 * share and bookmark between two dev servers.
 *
 * Anywhere else the URL is plain — `/manage-portfolio`. A deployed product has
 * its own hostname (cca.amd.com, eia.amd.com), which already says which product
 * it is, so a prefix would only repeat it. This is what legacy does too: its
 * getBasePath() adds '/cca/' or '/eia/' for dev ports and uses '/' on real hosts
 * (router/index.js:5-25).
 *
 * The route table itself stays prefix-free (one shared table for both products,
 * per the no-forking rule) — React Router adds and strips the basename, so
 * `navigate('/manage-portfolio')` and `useLocation().pathname` work unchanged
 * either way.
 *
 * Because the basename must be right in the address bar before the router
 * mounts, the entry URL is rewritten in place with history.replaceState — a
 * rewrite, not a navigation, so it costs no extra request and leaves no
 * back-button trap.
 */
import { isLocalHost } from '@utils/hosts';
import { VARIANTS } from '@variant/variants.config';

/**
 * The router basename for a mode on a host: '/cca' | '/eia' locally, '' (no
 * prefix) everywhere else.
 *
 * Takes the hostname rather than reading `window` itself, so it is directly
 * unit-testable; the default is the live host.
 *
 * @param {'cca' | 'eia'} mode
 * @param {string} [hostname]
 * @returns {'' | '/cca' | '/eia'}
 */
export function getBasename(
  mode,
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
) {
  return isLocalHost(hostname) ? `/${mode}` : '';
}

// Every prefix any variant claims — used to find one already in the URL.
const VARIANT_PREFIXES = Object.values(VARIANTS).flatMap((v) => v.match.pathPrefixes ?? []);

/**
 * Remove a leading variant prefix if there is one, leaving the real route.
 * Matches on a full segment boundary only, so '/ccafoo' is NOT '/cca'.
 *   '/eia'            -> '/'
 *   '/eia/portfolio'  -> '/portfolio'
 *   '/portfolio'      -> '/portfolio'
 */
export function stripVariantPrefix(pathname) {
  for (const p of VARIANT_PREFIXES) {
    if (pathname === p) return '/';
    if (pathname.startsWith(`${p}/`)) return pathname.slice(p.length);
  }
  return pathname;
}

/**
 * Make the current URL agree with the basename. Call once, before the router
 * is created.
 *
 *  - Local, basename '/cca': adds a missing prefix, and REPLACES one that names
 *    the other product. Locally the mode comes from the port — the server you
 *    are actually talking to — so a stale or hand-typed `/eia` on a CCA dev
 *    server has to be corrected, or that server would serve the wrong product.
 *  - Elsewhere, basename '': REMOVES any prefix, so an old bookmark such as
 *    `/cca/manage-portfolio` on a deployed host lands on `/manage-portfolio`
 *    instead of a page that does not exist.
 *
 * @param {'' | '/cca' | '/eia'} basename
 */
export function ensureVariantPrefix(basename) {
  if (typeof window === 'undefined') return;

  const { pathname, search, hash } = window.location;
  const route = stripVariantPrefix(pathname);
  const rest = route === '/' ? '' : route;
  // With no basename and nothing after it, the path is the root, not ''.
  const path = `${basename}${rest}` || '/';
  const next = `${path}${search}${hash}`;

  // Skip the write when the URL is already right — avoids a pointless history
  // entry rewrite on every boot.
  if (next === `${pathname}${search}${hash}`) return;

  window.history.replaceState(null, '', next);
}
